import { useEffect, useMemo, useRef, useState } from "react";
import { ButtonUtility, Dropdown, Input } from "@its-engineering-and-development/ui-foundation/components";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { Spinner } from "@app/components/base/spinner/spinner";
import { ConfirmDialog } from "@app/components/base/confirm-dialog";
import { DataTableShell } from "@app/components/base/data-table/data-table";
import { ColumnFilterPopover } from "@app/components/base/column-filter-popover";
import { ColumnHeader } from "@app/components/base/column-header";
import { useColumnFilter } from "@app/hooks/use-column-filter";
import { useInfiniteScrollOnVisible } from "@app/hooks/use-infinite-scroll-on-visible";
import {
    usePermissionsRoleUsersTable,
    toggleUsersSort,
    type PermissionsUsersByBatchFilter,
    type PermissionsUsersByBatchSort,
    type PermissionsUsersSortField,
} from "@app/hooks/use-permissions";
import type { PermissionRole, PermissionUser, RoleAssignment } from "@app/types/permissions";
import { ROLE_KEY } from "@app/types/permissions";
import { authClient } from "@app/api/auth-client";
import { useLogout } from "@app/hooks/use-logout";
import { PermissionUserAvatar } from "./permission-user-avatar";

const SELF_UNASSIGN_LOGOUT_MESSAGE = "Your session was closed because you removed a role from your own account. Please sign in again.";
const MANAGE_USERS_DRAWER_MAX_WIDTH = "640px";
const MANAGE_USERS_PAGE_LIMIT = 25;
const MANAGE_USERS_SEARCH_HISTORY_KEY = "expense-admin:permissions-manage-users-column-search-history";
type ManageUsersColumnKey = PermissionsUsersSortField;
/** Same primary/brand color used for every preview avatar (matches the assign-user dropdown). */
const PREVIEW_AVATAR_COLOR_INDEX = 3;

/** Up to 5 overlapping avatars for a role card preview, plus a "+N" badge when more are assigned. */
function RolePreviewAvatars({ users, isLoading, extraCount }: { users: PermissionUser[]; isLoading: boolean; extraCount: number }) {
    if (isLoading) {
        return (
            <div className="flex -space-x-1.5" aria-hidden="true">
                {[0, 1].map((key) => (
                    <div key={key} className="h-6 w-6 animate-pulse rounded-full border-2 border-white bg-gray-100" />
                ))}
            </div>
        );
    }

    if (users.length === 0) return null;

    return (
        <div className="flex items-center -space-x-1.5">
            {users.map((user) => (
                <PermissionUserAvatar key={user.id} user={user} colorIndex={PREVIEW_AVATAR_COLOR_INDEX} size="xs" className="ring-2 ring-white" />
            ))}
            {extraCount > 0 && (
                <span className="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600 ring-2 ring-white">
                    +{extraCount}
                </span>
            )}
        </div>
    );
}

const ASSIGN_USER_MENU_ITEM_CLASS = "[&>div>span]:!font-normal";

function AssignUserDropdown({
    roleLabel,
    availableUsers,
    assignedUserIds,
    isLoadingUsers,
    hasNextUsersPage,
    onLoadMore,
    fetchUsersByName,
    onAssign,
}: {
    roleLabel: string;
    availableUsers: PermissionUser[];
    assignedUserIds: string[];
    isLoadingUsers: boolean;
    hasNextUsersPage: boolean;
    onLoadMore: () => void;
    fetchUsersByName: (name: string) => Promise<PermissionUser[]>;
    onAssign: (userId: string) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const hasSelectedRef = useRef(false);
    const isOpenRef = useRef(false);
    isOpenRef.current = isOpen;
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<PermissionUser[] | null>(null);
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    // Focus the search input — used on open and after results arrive
    const focusSearchInput = () => searchInputRef.current?.querySelector<HTMLElement>("input")?.focus();

    // Auto-focus when the popover opens (setTimeout lets React Aria settle first)
    useEffect(() => {
        if (!isOpen) return;
        const id = setTimeout(focusSearchInput, 0);
        return () => clearTimeout(id);
    }, [isOpen]);

    // Re-focus after search results arrive — Dropdown.Menu mount may steal focus
    useEffect(() => {
        if (isSearchingUsers || !isOpen) return;
        const id = setTimeout(focusSearchInput, 0);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearchingUsers]);

    // Server-side search with 300 ms debounce
    useEffect(() => {
        const query = searchQuery.trim();
        if (!query) {
            setSearchResults(null);
            setIsSearchingUsers(false);
            return;
        }

        let stale = false;
        const timer = setTimeout(() => {
            setIsSearchingUsers(true);
            fetchUsersByName(query)
                .then((results) => {
                    if (!stale) setSearchResults(results);
                })
                .catch(() => {
                    if (!stale) setSearchResults([]);
                })
                .finally(() => {
                    if (!stale) setIsSearchingUsers(false);
                });
        }, 300);

        return () => {
            stale = true;
            clearTimeout(timer);
        };
    }, [searchQuery, fetchUsersByName]);

    // Infinite-scroll sentinel — only active when not in search mode
    const isSearchActive = searchQuery.trim().length > 0;

    useInfiniteScrollOnVisible(sentinelRef, {
        hasNextPage: hasNextUsersPage,
        isLoading: isLoadingUsers,
        disabled: isSearchActive,
        onLoadMore,
    });

    // Display list: server results (filtered for already-assigned) OR pre-filtered base list
    const displayUsers = useMemo(() => {
        if (searchResults !== null) {
            return searchResults.filter((user) => !assignedUserIds.includes(user.id));
        }
        return availableUsers;
    }, [searchResults, availableUsers, assignedUserIds]);

    return (
        <div className="w-fit self-start">
            <Dropdown.Root
                isOpen={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);
                    if (open) {
                        hasSelectedRef.current = false;
                    } else {
                        setSearchQuery("");
                        setSearchResults(null);
                        setIsSearchingUsers(false);
                    }
                }}
            >
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => {
                        hasSelectedRef.current = false;
                        setIsOpen(true);
                    }}
                    className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                        />
                    </svg>
                    Assign user
                </button>

                <Dropdown.Popover triggerRef={triggerRef} placement="bottom start" offset={8} className="w-64 overflow-hidden p-0">
                    <div
                        ref={searchInputRef}
                        className="border-b border-gray-200 p-2"
                        onBlur={(e) => {
                            const related = e.relatedTarget as HTMLElement | null;
                            // React Aria moves focus to menu items on hover — intercept and restore
                            if (related?.closest('[role="menu"]')) {
                                setTimeout(() => {
                                    if (isOpenRef.current) focusSearchInput();
                                }, 0);
                            }
                        }}
                    >
                        <Input
                            aria-label={`Search users to assign to ${roleLabel}`}
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(value) => setSearchQuery(String(value ?? ""))}
                            size="sm"
                        />
                    </div>

                    {isSearchingUsers ? (
                        <div className="flex items-center justify-center py-6">
                            <Spinner className="h-5 w-5 text-gray-400" />
                        </div>
                    ) : (
                        <Dropdown.Menu
                            aria-label={`Available users for ${roleLabel}`}
                            className="max-h-60"
                            selectionMode="none"
                            disallowEmptySelection={false}
                            onAction={(key) => {
                                if (key === "no-results" || key === "__sentinel__") return;
                                if (hasSelectedRef.current) return;
                                hasSelectedRef.current = true;
                                setIsOpen(false);
                                setSearchQuery("");
                                setSearchResults(null);
                                onAssign(String(key));
                            }}
                        >
                            {displayUsers.length === 0 ? (
                                <Dropdown.Item key="no-results" id="no-results" isDisabled label="No users found" className={ASSIGN_USER_MENU_ITEM_CLASS} />
                            ) : (
                                displayUsers.map((user) => (
                                    <Dropdown.Item key={user.id} id={user.id} textValue={user.name} className={ASSIGN_USER_MENU_ITEM_CLASS}>
                                        <span className="inline-flex items-center gap-2">
                                            <PermissionUserAvatar user={user} colorIndex={3} size="xs" className="!size-6" />
                                            <span>{user.name}</span>
                                        </span>
                                    </Dropdown.Item>
                                ))
                            )}
                            {!isSearchActive && (isLoadingUsers || hasNextUsersPage) && (
                                <Dropdown.Item key="__sentinel__" id="__sentinel__" isDisabled textValue="">
                                    <div ref={sentinelRef} className="flex w-full justify-center py-0.5">
                                        {isLoadingUsers && <Spinner className="h-4 w-4 text-brand-500" />}
                                    </div>
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    )}
                </Dropdown.Popover>
            </Dropdown.Root>
        </div>
    );
}

function RoleAssignmentCard({
    role,
    assignment,
    allUsers,
    previewUsers,
    isRoleLoading,
    pendingCount,
    isLoadingUsers,
    hasNextUsersPage,
    onLoadMoreUsers,
    fetchUsersByName,
    onAssign,
    onManageUsers,
    fullWidth = false,
    canEdit = false,
}: {
    role: PermissionRole;
    assignment: RoleAssignment;
    allUsers: PermissionUser[];
    previewUsers: PermissionUser[];
    isRoleLoading: boolean;
    pendingCount: number;
    isLoadingUsers: boolean;
    hasNextUsersPage: boolean;
    onLoadMoreUsers: () => void;
    fetchUsersByName: (name: string) => Promise<PermissionUser[]>;
    onAssign: (userId: string) => void;
    onManageUsers: () => void;
    fullWidth?: boolean;
    canEdit?: boolean;
}) {
    const availableUsers = useMemo(() => allUsers.filter((user) => !assignment.userIds.includes(user.id)), [allUsers, assignment.userIds]);

    const totalCount = assignment.userIds.length;
    const extraCount = Math.max(totalCount - previewUsers.length, 0);
    const userCountLabel = `${totalCount} user${totalCount === 1 ? "" : "s"}`;

    return (
        <article className={cx("flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-xs", fullWidth && "col-span-full")}>
            <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-semibold text-gray-900">{role.label}</h3>
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                    {userCountLabel}
                    {pendingCount > 0 && <Spinner className="h-3.5 w-3.5 text-gray-400" />}
                </span>
            </div>

            <p className="text-sm leading-relaxed text-gray-600">{assignment.description}</p>

            <div className="flex items-center gap-3">
                <RolePreviewAvatars users={previewUsers} isLoading={isRoleLoading} extraCount={extraCount} />
                {!isRoleLoading && totalCount > 0 && (
                    <button type="button" className="text-sm font-medium text-blue-600 hover:text-blue-700" onClick={onManageUsers}>
                        Manage users
                    </button>
                )}
            </div>

            {canEdit && availableUsers.length > 0 ? (
                <AssignUserDropdown
                    roleLabel={role.label}
                    availableUsers={availableUsers}
                    assignedUserIds={assignment.userIds}
                    isLoadingUsers={isLoadingUsers}
                    hasNextUsersPage={hasNextUsersPage}
                    onLoadMore={onLoadMoreUsers}
                    fetchUsersByName={fetchUsersByName}
                    onAssign={onAssign}
                />
            ) : null}
        </article>
    );
}

/** "Name"/"Email" table used inside the Manage Users drawer — same styling as the Group Members table.
 * Filtering and sorting are both supported server-side (per role-user hydration doc).
 */
function ManageUsersTable({
    users,
    isLoading,
    pageInfo,
    onPageChange,
    pendingIds,
    canEdit,
    onRemoveClick,
    filter,
    onFilter,
    sort,
    onSort,
}: {
    users: PermissionUser[];
    isLoading: boolean;
    pageInfo: { totalCount: number; offset: number; limit: number; hasNextPage: boolean };
    onPageChange: (page: number) => void;
    pendingIds: Set<string>;
    canEdit: boolean;
    onRemoveClick: (user: PermissionUser) => void;
    filter: PermissionsUsersByBatchFilter;
    onFilter: (filter: PermissionsUsersByBatchFilter) => void;
    sort?: PermissionsUsersByBatchSort;
    onSort: (sort: PermissionsUsersByBatchSort) => void;
}) {
    const { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved } =
        useColumnFilter<ManageUsersColumnKey>(MANAGE_USERS_SEARCH_HISTORY_KEY);

    const applyColumnFilter = (key: ManageUsersColumnKey, value?: string) => {
        const v = (value ?? filterInput).trim();
        if (!v) return;
        onFilter({ ...filter, [key]: v });
        closeFilter();
    };

    const clearColumnFilter = (key: ManageUsersColumnKey) => {
        onFilter({ ...filter, [key]: undefined });
        closeFilter();
    };

    const hasActiveFilters = Boolean(filter.name || filter.email);

    const handleSort = (key: ManageUsersColumnKey) => {
        onSort(toggleUsersSort(sort, key));
    };

    const colHeader = (key: ManageUsersColumnKey, label: string) => {
        const isFilterActive = Boolean(filter[key]);
        const isSortActive = sort?.sortBy === key;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable
                isFilterActive={isFilterActive}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl, filter[key] ?? "")}
                isSortable
                isSortActive={isSortActive}
                sortDirection={isSortActive ? sort?.sortOrder : undefined}
                onSort={() => handleSort(key)}
            />,
            <ColumnFilterPopover
                key={`${key}-filter`}
                isOpen={activeFilterColumn === key}
                anchorEl={activeFilterAnchor}
                columnLabel={label}
                searchValue={filterInput}
                savedValues={searchHistory[key] ?? []}
                onSearchChange={setFilterInput}
                onClose={closeFilter}
                onApply={(value) => applyColumnFilter(key, value)}
                onClear={isFilterActive ? () => clearColumnFilter(key) : undefined}
                onRemoveSavedValue={removeSaved}
            />,
        ];
    };

    return (
        <DataTableShell
            tabName="users"
            isLoading={isLoading}
            minWidth="0px"
            isEmpty={users.length === 0}
            hasActiveFilters={hasActiveFilters}
            emptyMessage="No users found."
            pageInfo={{
                totalCount: pageInfo.totalCount,
                offset: pageInfo.offset,
                limit: pageInfo.limit,
                hasNextPage: pageInfo.hasNextPage,
            }}
            onPageChange={onPageChange}
            header={
                <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                    <tr>
                        {colHeader("name", "Name")}
                        {colHeader("email", "Email")}
                        {canEdit && <th className="w-14 px-4 py-3" />}
                    </tr>
                </thead>
            }
        >
            <tbody className="divide-y divide-gray-100">
                {users.map((user) => {
                    const isPending = pendingIds.has(user.id);
                    return (
                        <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-medium text-gray-900">{user.name}</td>
                            <td className="px-4 py-2.5 text-gray-500">{user.emailId ?? "—"}</td>
                            {canEdit && (
                                <td className="px-4 py-2.5 text-right">
                                    {isPending ? (
                                        <span className="inline-flex h-6 w-6 items-center justify-center">
                                            <Spinner className="h-3.5 w-3.5 text-gray-400" />
                                        </span>
                                    ) : (
                                        <ButtonUtility
                                            aria-label={`Remove ${user.name}`}
                                            tooltip="Remove from role"
                                            size="xs"
                                            color="tertiary"
                                            className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                                            onClick={() => onRemoveClick(user)}
                                            icon={
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            }
                                        />
                                    )}
                                </td>
                            )}
                        </tr>
                    );
                })}
            </tbody>
        </DataTableShell>
    );
}

/** "Manage users" drawer for a role — mirrors the Group Members drawer styling (fixed overlay, no shared Drawer component). */
function PermissionsRoleUsersDrawer({
    role,
    assignment,
    canEdit,
    currentUserId,
    pendingUserIds,
    onUnassign,
    onClose,
}: {
    role: PermissionRole;
    assignment: RoleAssignment;
    canEdit: boolean;
    currentUserId: string | null;
    pendingUserIds: Set<string>;
    onUnassign: (userId: string) => Promise<void> | void;
    onClose: () => void;
}) {
    const logout = useLogout();
    const [filter, setFilter] = useState<PermissionsUsersByBatchFilter>({});
    const [sort, setSort] = useState<PermissionsUsersByBatchSort | undefined>(undefined);
    const { users, isLoading, goToPage, pageInfo } = usePermissionsRoleUsersTable(assignment.userIds, MANAGE_USERS_PAGE_LIMIT, filter, sort);

    const [pendingUnassign, setPendingUnassign] = useState<{ userId: string; userName: string } | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [pendingSelfUnassign, setPendingSelfUnassign] = useState<{ userId: string; userName: string } | null>(null);
    const [isSelfConfirming, setIsSelfConfirming] = useState(false);

    const needsConfirmation = role.roleKey === ROLE_KEY.Approver || role.roleKey === ROLE_KEY.GroupAccountingApprover;
    const hasActiveFilter = Boolean(filter.name || filter.email);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const handleRemoveClick = (user: PermissionUser) => {
        if (currentUserId && user.id === currentUserId) {
            setPendingSelfUnassign({ userId: user.id, userName: user.name });
            return;
        }
        if (needsConfirmation) {
            setPendingUnassign({ userId: user.id, userName: user.name });
        } else {
            void onUnassign(user.id);
        }
    };

    const userCountLabel = hasActiveFilter
        ? `${pageInfo.totalCount} of ${assignment.userIds.length} user${assignment.userIds.length === 1 ? "" : "s"}`
        : `${assignment.userIds.length} user${assignment.userIds.length === 1 ? "" : "s"}`;

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`${role.label} users`}
                style={{ maxWidth: MANAGE_USERS_DRAWER_MAX_WIDTH }}
                className="fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden bg-white shadow-2xl"
            >
                <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                    <div>
                        <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Role</p>
                        <h2 className="text-xl font-semibold text-gray-900">{role.label}</h2>
                    </div>
                    <button type="button" aria-label="Close" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" onClick={onClose}>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
                    <span className="mb-3 text-sm text-gray-500">{userCountLabel}</span>
                    <ManageUsersTable
                        users={users}
                        isLoading={isLoading}
                        pageInfo={pageInfo}
                        onPageChange={goToPage}
                        pendingIds={pendingUserIds}
                        canEdit={canEdit}
                        onRemoveClick={handleRemoveClick}
                        filter={filter}
                        onFilter={setFilter}
                        sort={sort}
                        onSort={setSort}
                    />
                </div>
            </div>

            {needsConfirmation && (
                <ConfirmDialog
                    isOpen={pendingUnassign !== null}
                    onOpenChange={(open) => {
                        if (!open) setPendingUnassign(null);
                    }}
                    title={pendingUnassign ? `Remove ${pendingUnassign.userName} from ${role.label}?` : ""}
                    confirmLabel="Confirm"
                    busyLabel="Removing..."
                    isConfirming={isConfirming}
                    onConfirm={async () => {
                        if (!pendingUnassign) return;
                        setIsConfirming(true);
                        try {
                            await onUnassign(pendingUnassign.userId);
                        } finally {
                            setIsConfirming(false);
                            setPendingUnassign(null);
                        }
                    }}
                >
                    {role.roleKey === ROLE_KEY.Approver
                        ? "This user will be removed as an Approver from all groups they were assigned to."
                        : "This user will be removed as a Group Accounting Approver from all groups they were assigned to."}
                </ConfirmDialog>
            )}

            {pendingSelfUnassign && (
                <ConfirmDialog
                    isOpen={pendingSelfUnassign !== null}
                    onOpenChange={(open) => {
                        if (!open) setPendingSelfUnassign(null);
                    }}
                    title="Remove a role from your own account?"
                    confirmLabel="Remove and sign out"
                    busyLabel="Removing..."
                    isConfirming={isSelfConfirming}
                    onConfirm={async () => {
                        if (!pendingSelfUnassign) return;
                        setIsSelfConfirming(true);
                        try {
                            await onUnassign(pendingSelfUnassign.userId);
                        } finally {
                            await logout({ message: SELF_UNASSIGN_LOGOUT_MESSAGE });
                        }
                    }}
                >
                    You are about to remove the {role.label} role from your own account. Because this changes your own permissions, your session will be closed
                    and you will need to sign in again.
                </ConfirmDialog>
            )}
        </>
    );
}

export function UserAssignmentsSection({
    roles,
    roleAssignments,
    rolePreviewUsersByRoleId,
    users,
    loadingRoleIds,
    pendingUserIds,
    isLoadingUsers,
    hasNextUsersPage,
    onLoadMoreUsers,
    fetchUsersByName,
    onAssign,
    onUnassign,
    canEdit,
}: {
    roles: PermissionRole[];
    roleAssignments: RoleAssignment[];
    rolePreviewUsersByRoleId: Map<string, PermissionUser[]>;
    users: PermissionUser[];
    loadingRoleIds: Set<string>;
    pendingUserIds: Map<string, Set<string>>;
    isLoadingUsers: boolean;
    hasNextUsersPage: boolean;
    onLoadMoreUsers: () => void;
    fetchUsersByName: (name: string) => Promise<PermissionUser[]>;
    onAssign: (roleId: string, userId: string) => void;
    onUnassign: (roleId: string, userId: string) => Promise<void> | void;
    canEdit: boolean;
}) {
    const currentUserId = useMemo(() => {
        try {
            const decoded = authClient.decodeToken();
            return decoded?.userGuid ?? decoded?.sub ?? null;
        } catch {
            return null;
        }
    }, []);
    const lastCardSpansFullWidth = roleAssignments.length % 2 === 1;
    const [manageUsersRoleId, setManageUsersRoleId] = useState<string | null>(null);

    const manageUsersRole = roles.find((entry) => entry.id === manageUsersRoleId) ?? null;
    const manageUsersAssignment = roleAssignments.find((entry) => entry.roleId === manageUsersRoleId) ?? null;

    return (
        <section className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold text-gray-900">User assignments</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {roleAssignments.map((assignment, index) => {
                    const role = roles.find((entry) => entry.id === assignment.roleId);
                    if (!role) return null;

                    const isLastCard = index === roleAssignments.length - 1;

                    return (
                        <RoleAssignmentCard
                            key={assignment.roleId}
                            role={role}
                            assignment={assignment}
                            allUsers={users}
                            previewUsers={rolePreviewUsersByRoleId.get(assignment.roleId) ?? []}
                            isRoleLoading={loadingRoleIds.has(assignment.roleId)}
                            pendingCount={pendingUserIds.get(assignment.roleId)?.size ?? 0}
                            isLoadingUsers={isLoadingUsers}
                            hasNextUsersPage={hasNextUsersPage}
                            onLoadMoreUsers={onLoadMoreUsers}
                            fetchUsersByName={fetchUsersByName}
                            fullWidth={lastCardSpansFullWidth && isLastCard}
                            onAssign={(userId) => onAssign(assignment.roleId, userId)}
                            onManageUsers={() => setManageUsersRoleId(assignment.roleId)}
                            canEdit={canEdit}
                        />
                    );
                })}
            </div>

            {manageUsersRole && manageUsersAssignment && (
                <PermissionsRoleUsersDrawer
                    role={manageUsersRole}
                    assignment={manageUsersAssignment}
                    canEdit={canEdit}
                    currentUserId={currentUserId}
                    pendingUserIds={pendingUserIds.get(manageUsersRole.id) ?? new Set()}
                    onUnassign={(userId) => onUnassign(manageUsersRole.id, userId)}
                    onClose={() => setManageUsersRoleId(null)}
                />
            )}
        </section>
    );
}
